import React from 'react';
import { Paper, Typography, Stack, Chip, Box, useTheme } from '@mui/material';
import {
  CalendarToday as DateIcon,
  Category as TypeIcon,
  Star as ScoreIcon,
  Chat,
} from '@mui/icons-material';
import { format } from 'date-fns';

type Match = {
  content: string;
  score?: number;
  metadata: {
    chatId: string;
    createdAt: string;
    updatedAt: string;
    type: string;
  };
};

type Props = {
  topMatches: Match[];
  onMatchClick: (matchId: string) => void;
};

const ChatTopMatches: React.FC<Props> = ({ topMatches, onMatchClick }) => {
  const theme = useTheme();
  if (!topMatches?.length) return null;

  return (
    <Stack spacing={1.5} sx={{ px: 2, py: 1 }}>
      <Typography
        variant="h6"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: theme.palette.text.secondary,
        }}
      >
        <ScoreIcon color="primary" /> Top Matches
      </Typography>

      {topMatches.map(match => (
        <Paper
          key={match.metadata.chatId}
          elevation={0}
          sx={{
            p: 2,
            cursor: 'pointer',
            borderLeft: '2px solid transparent',
            transition: 'all .15s',
            '&:hover': {
              bgcolor: theme.palette.action.hover,
              borderLeftColor: theme.palette.primary.main,
            },
            display: 'flex',
            gap: 1.5,
            alignItems: 'flex-start',
          }}
          onClick={() => onMatchClick(match.metadata.chatId)}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                fontSize: '1rem',
                lineHeight: 1.4,
              }}
            >
              {match.content}
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 0.5,
              }}
            >
              <Chat fontSize="inherit" />
              {match.metadata.chatId}
            </Typography>
          </Box>

          <Box sx={{ flexShrink: 0, textAlign: 'right' }}>
            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
              <Chip
                icon={<TypeIcon />}
                label={match.metadata.type}
                size="small"
                variant="outlined"
              />
              {match.score != null && (
                <Chip
                  icon={<ScoreIcon />}
                  label={match.score.toFixed(2)}
                  size="small"
                  color={match.score > 0.7 ? 'primary' : 'default'}
                  variant="outlined"
                />
              )}
            </Stack>

            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 0.5,
              }}
            >
              <DateIcon fontSize="inherit" />
              {format(new Date(match.metadata.updatedAt), 'MMM d, yyyy')}
            </Typography>
          </Box>
        </Paper>
      ))}
    </Stack>
  );
};

export default ChatTopMatches;
